/**
 * FileManager
 *
 * Main file management page component. Scope tabs, view toggle, search, upload zone,
 * file list/grid, and detail panel.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Search, Grid, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { useFileStore } from '@/stores/files/file-store';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { FileListParams, FileRecord, FileService } from '@/services/api/fileService';
import { FileUploadZone } from './FileUploadZone';
import { FileList } from './FileList';
import { FileDetailPanel } from './FileDetailPanel';
import * as errorUtils from '@/utils/errors';

export const FileManager: React.FC = () => {
  const orgSlug = useOrganizationStore((state) =>
    state.currentContext?.type === 'organization' ? state.currentContext.orgSlug || '' : ''
  );

  const {
    files, isLoading, error, pagination, usage,
    fetchFiles, fetchUsage, deleteFile, setSelectedFile, selectedFileId,
  } = useFileStore();

  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [scope, setScope] = useState<'all' | 'private' | 'project' | 'shared'>('all');
  const [search, setSearch] = useState('');
  const [selectedFile, setSelectedFileLocal] = useState<FileRecord | null>(null);

  const handleScopeChange = useCallback((value: string) => {
    if (value === 'all' || value === 'private' || value === 'project' || value === 'shared') {
      setScope(value);
    }
  }, []);

  // Fetch on mount and scope/search change
  useEffect(() => {
    if (!orgSlug) return;
    const params: FileListParams = {
      page: pagination.page,
      limit: pagination.limit,
    };
    if (scope !== 'all') params.scope = scope;
    if (search) params.search = search;
    fetchFiles(orgSlug, params);
  }, [orgSlug, scope, search, pagination.page]);

  // Fetch usage on mount
  useEffect(() => {
    if (orgSlug) fetchUsage(orgSlug);
  }, [orgSlug]);

  const handleSelect = useCallback((file: FileRecord) => {
    setSelectedFileLocal(file);
    setSelectedFile(file._id);
  }, [setSelectedFile]);

  const handleDownload = useCallback(async (file: FileRecord) => {
    if (file.encryptionType === 'user_protected') {
      // Detail panel handles passphrase dialog
      setSelectedFileLocal(file);
      return;
    }
    try {
      const response = await FileService.getDownloadUrl(orgSlug, file._id);
      if (response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      }
    } catch (error: unknown) {
      alert(errorUtils.getErrorMessageOrFallback(error, 'Download failed'));
    }
  }, [orgSlug]);

  const handleDelete = useCallback(async (file: FileRecord) => {
    if (!confirm(`Delete "${file.filename}"?`)) return;
    try {
      await deleteFile(orgSlug, file._id);
      if (selectedFile?._id === file._id) setSelectedFileLocal(null);
    } catch (error: unknown) {
      alert(errorUtils.getErrorMessageOrFallback(error, 'Delete failed'));
    }
  }, [orgSlug, deleteFile, selectedFile]);

  const handleUploadComplete = useCallback(() => {
    fetchFiles(orgSlug, {
      page: 1,
      limit: pagination.limit,
      ...(scope !== 'all' ? { scope } : {}),
    });
    fetchUsage(orgSlug);
  }, [orgSlug, scope, pagination.limit, fetchFiles, fetchUsage]);

  if (!orgSlug) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Select an organization to manage files.
      </div>
    );
  }

  return (
    <div className="fixed inset-0 top-[73px] bg-background flex flex-col">
      <Tabs value={scope} onValueChange={handleScopeChange} className="flex flex-col h-full">
        {/* Header */}
        <div className="flex-shrink-0 border-b p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">Files</h1>
            {usage && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Progress value={usage.usagePercentage} className="w-32 h-2" />
                <span>{usage.usagePercentage.toFixed(1)}% used</span>
              </div>
            )}
          </div>

          {/* Scope Tabs + Search + View Toggle */}
          <div className="flex items-center justify-between gap-4">
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="shared">Shared</TabsTrigger>
              <TabsTrigger value="project">Project</TabsTrigger>
              <TabsTrigger value="private">Private</TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-2 flex-1 max-w-md">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search files..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-8"
                />
              </div>
              <Button
                size="sm"
                variant={view === 'grid' ? 'default' : 'outline'}
                className="h-8 w-8 p-0"
                onClick={() => setView('grid')}
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant={view === 'list' ? 'default' : 'outline'}
                className="h-8 w-8 p-0"
                onClick={() => setView('list')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {/* Upload Zone */}
          <FileUploadZone
            orgSlug={orgSlug}
            scope={scope === 'all' ? 'private' : scope}
            onUploadComplete={handleUploadComplete}
          />

          {/* Error */}
          {error && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded">{error}</div>
          )}

          {/* File List (same content for all tab values) */}
          {isLoading ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">Loading...</div>
          ) : (
            <FileList
              files={files}
              view={view}
              onSelect={handleSelect}
              onDownload={handleDownload}
              onDelete={handleDelete}
            />
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2 py-2">
              <Button
                size="sm"
                variant="outline"
                disabled={pagination.page <= 1}
                onClick={() => useFileStore.setState((s) => ({ pagination: { ...s.pagination, page: s.pagination.page - 1 } }))}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.pages}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={pagination.page >= pagination.pages}
                onClick={() => useFileStore.setState((s) => ({ pagination: { ...s.pagination, page: s.pagination.page + 1 } }))}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </Tabs>

      {/* Detail Panel */}
      <FileDetailPanel
        file={selectedFile}
        orgSlug={orgSlug}
        onClose={() => { setSelectedFileLocal(null); setSelectedFile(null); }}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default FileManager;
